import type { ReactElement } from 'react';
import { useState } from 'react';
import { MenuButton } from '../ui';
import { useNotices } from '../ui/Notices';
import { useUpdateCommitment } from '../api/queries';
import { dateLabel, daysUntil, dueLabel, inDays, nextWeekday } from '../lib/time';

/**
 * Setting a deadline in one press.
 *
 * Vladimir, 2026-09-22: *"I want to be able to quickly add optional deadline
 * for the task and this will allow to better showing actual for now tasks."*
 * The point is the ordering it buys, so the cost of setting one has to be near
 * zero — presets first, a date field only for the rest, and taking one off is
 * as easy as putting one on.
 *
 * ⚠ It stays optional and is never guessed. Most of what is promised in a
 * meeting has no date, and a product that invents them is a product whose
 * dates nobody believes.
 *
 * ## Why it takes an id and a date rather than a whole item
 *
 * It lived inside the Open page and could only be handed an `OpenItem`, so the
 * meeting a commitment was MADE in — where it is read at least as often —
 * could not offer it. Vladimir, 2026-09-23: *"add deadline set to every place
 * where commitments are as in the meetings I can't sent deadline and I need
 * it."* It now takes the two things it actually uses, so every page that holds
 * a commitment can offer it.
 */
export function DueButton({
  id,
  dueAt,
  size = 'sm',
}: {
  id: string;
  dueAt: string | null;
  size?: 'sm' | 'md';
}): ReactElement {
  const set = useUpdateCommitment();
  const { show } = useNotices();
  const [picking, setPicking] = useState(false);
  const days = dueAt ? daysUntil(dueAt) : null;
  const late = days !== null && days < 0;
  const soon = days !== null && days <= 2;

  const save = (next: string | null) =>
    set.mutate(
      { id, body: { dueAt: next } },
      {
        onSuccess: () => {
          setPicking(false);
          show({
            tone: 'success',
            text: next ? `Due ${dateLabel(next)}.` : 'No deadline on it now.',
          });
        },
        onError: (error) => show({ tone: 'failure', text: error.message }),
      },
    );

  if (picking) {
    return (
      <input
        type="date"
        className="field"
        aria-label="Due date"
        autoFocus
        defaultValue={dueAt ? dueAt.slice(0, 10) : ''}
        style={{ height: 28, width: 150 }}
        onBlur={() => setPicking(false)}
        onChange={(e) =>
          e.target.value ? save(new Date(`${e.target.value}T09:00:00`).toISOString()) : undefined
        }
      />
    );
  }

  return (
    <MenuButton
      size={size}
      variant="quiet"
      label={
        <span className={late ? 'red' : soon ? 'amber' : undefined}>
          {dueAt ? dueLabel(dueAt) : '+ deadline'}
        </span>
      }
      items={[
        { label: 'Today', onSelect: () => save(inDays(0)) },
        { label: 'Tomorrow', onSelect: () => save(inDays(1)) },
        { label: 'Friday', onSelect: () => save(nextWeekday(5)) },
        { label: 'Next Monday', onSelect: () => save(nextWeekday(1)) },
        { label: 'Pick a date…', onSelect: () => setPicking(true) },
        ...(dueAt ? [{ label: 'No deadline', onSelect: () => save(null), danger: true }] : []),
      ]}
    />
  );
}
